<template>
  <v-card :loading="loadingCard">
    <v-card-title>
      <v-row justify="space-between">
        <span class="font-weight-light" style="font-size: 16pt">{{ $t('label.poDetails') + " (" + purchaseOrderNumber + ")" }}</span>
          <v-btn :disabled="loadingCard" icon @click.stop="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <!-- <v-row no-gutters>
        <v-col cols="12" sm="6" md="2">
          <v-btn :disabled="loadingCard || isLoading" color="white" depressed large @click.stop="openUploadFileDialog('purchaseOrder')">
            <v-icon color="#00469C" size="70">mdi-cloud-upload</v-icon>
          </v-btn>
        </v-col>
      </v-row> -->
      <br>
      <v-data-table
        :headers="headers"
        :items="purchaseOrder.details"
        :loading="isLoading"
        :options="options"
        single-expand
        :expanded.sync="expanded"
        item-key="line"
        show-expand
        @item-expanded="onExpanded"
      >
        <template v-slot:[`item.options`]="{ item }">
          <v-btn small depressed :title="item.partNumber" @click.stop="openUploadFileDialog('purchaseOrderLine', item)">
            <v-icon color="primary">mdi-file-upload</v-icon> {{ $t('label.invoice') }}
          </v-btn>
        </template>
        <template v-slot:[`item.statusId`]="{ item }">
          <v-chip
            :color="getStatusColor(item.statusId)"
            >
            {{ getStatusName(item.statusId) }}
          </v-chip>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <v-data-table
              :headers="headersExpanded"
              :items="item.invoices"
              hide-default-footer
              class="expanded-row"
              :loading="loading.invoices"
            >
              <template v-slot:[`item.statusId`]="{ item }">
                <v-chip
                  :color="getInvoiceStatusColor(item.statusId)"
                  >
                  {{ getInvoiceStatusName(item.statusId) }}
                </v-chip>
              </template>
              <template v-slot:[`item.options`]="{ item }">
                <v-btn class="btn-action" icon :title="$t('label.pdf')" small depressed @click.stop="downloadPdf(item)">
                  <v-icon color="red">mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn v-if="!item.isPdf" class="btn-action" icon :title="$t('label.xml')" small depressed @click.stop="downloadXml(item)">
                  <v-icon color="primary">mdi-xml</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
      <v-row justify="end">
      <v-col cols="3">
        <h1>{{ $t('label.total') }} {{ formatCurrency(purchaseOrder.total) }}</h1>
      </v-col>
    </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn small depressed @click.stop="close" :disabled="loadingCard">{{ $t('label.close') }}</v-btn>
    </v-card-actions>

    <v-dialog v-model="isUploadFileDialogOpen" max-width="600" persistent>
      <v-card>
        <v-card-title>
          <v-row justify="space-between">
            <span class="font-weight-light" style="font-size: 16pt">{{ uploadFileTitle }}</span>
              <v-btn icon @click.stop="isUploadFileDialogOpen = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
          </v-row>
        </v-card-title>
        <v-card-text>
          <file-drop
            @filesSelected="filesSelected"
            accept="zip,application/zip,application/x-zip,application/x-zip-compressed,application/pdf"
            extensions="zip,pdf"
            >
          </file-drop>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog max-width="500" v-model="isUploadFileConfirmationDialogOpen" persistent>
      <confirmation-dialog @confirm="confirmUploadFile">
        {{ $t('message.confirmation.uploadInvoice') }}
      </confirmation-dialog>
    </v-dialog>
  </v-card>
</template>

<script>
import { formatCurrency } from '../../../core/sharedFunctions'
import { loading, showMessage } from '../../../mixins'
import FileDrop from '../../Shared/FileDrop'
import ConfirmationDialog from '../../Shared/ConfirmationDialog'
import { detailState, invoiceState } from '../../../core/PurchaseOrders/states'
import { saveAs } from 'file-saver'

export default {
  name: 'PurchaseOrderDetail',
  props: {
    purchaseOrderId: Number,
    isActive: Boolean,
    purchaseOrderNumber: {
      type: String,
      default: '0'
    }
  },
  mixins: [loading, showMessage],
  components: { FileDrop, ConfirmationDialog },
  data: () => ({
    purchaseOrder: {
      details: []
    },
    search: '',
    expanded: [],
    options: {
      sortBy: ['line'],
      sortDesc: [false],
      itemsPerPage: 10
    },
    selectedDetail: null,
    invoiceFile: null,
    isUploadFileDialogOpen: false,
    uploadFileFor: '',
    detailSelected: {},
    isUploadFileConfirmationDialogOpen: false,
    filesToUpload: [],
    loadingCard: false,
    loading: {
      invoices: false
    }
  }),
  computed: {
    headers () {
      const headers = [
        { text: this.$t('label.line'), value: 'line', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.partNumber'), value: 'partNumber', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.description'), value: 'description', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.uom'), value: 'unitOfMeasurement', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.status'), value: 'statusId', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.units'), value: 'units', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.unitsReceived'), value: 'unitsReceived', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.pricePerUnit'), value: 'pricePerUnit', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.subTotal'), value: 'subTotal', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.options'), value: 'options', align: 'center', sortable: true, filterable: true }
      ]
      return headers
    },
    headersExpanded () {
      const headers = [
        { text: this.$t('label.invoice'), value: 'invoiceNumber', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.invoiceSeries'), value: 'invoiceSeries', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.pdfFileName'), value: 'pdfFileName', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.xmlFileName'), value: 'xmlFileName', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.subTotal'), value: 'subTotal', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.total'), value: 'total', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.taxTotal'), value: 'taxTotal', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.status'), value: 'statusId', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.item'), value: 'item', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.quantity'), value: 'quantity', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.pricePerUnit'), value: 'pricePerUnit', align: 'center', sortable: true, filterable: true },
        // { text: this.$t('label.status'), value: 'statusId', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.options'), value: 'options', align: 'center', sortable: true, filterable: true }
      ]
      return headers
    },
    uploadFileTitle () {
      switch (this.uploadFileFor) {
        case 'purchaseOrder':
          return this.$t('label.uploadInvoiceForPurchaseOrder')
        case 'purchaseOrderLine':
          return this.$t('label.uploadInvoiceForPurchaseOrderLine')
        default:
          return ''
      }
    }
  },
  methods: {
    getPurchaseOrderDetail () {
      this.changeLoadingStatus(true)
      this.$proxy.get(`api/purchaseorders/${this.purchaseOrderId}`)
        .then((r) => {
          this.purchaseOrder = r.data
          this.purchaseOrder.details = r.data.details.map((d) => {
            d.subTotal = formatCurrency(d.subTotal)
            d.pricePerUnit = formatCurrency(d.pricePerUnit)
            if (!d.invoices) {
              d.invoices = []
            }
            // else {
            //   d.detailInvoices = d.detailInvoices.map((i) => {
            //     i.pricePerUnit = formatCurrency(i.pricePerUnit)
            //     return i
            //   })
            // }
            return d
          })
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.changeLoadingStatus(false)
        })
    },
    close () {
      this.expanded = []
      this.$emit('close')
    },
    openUploadFileDialog (uploadFileFor, detailSelected = {}) {
      this.uploadFileFor = uploadFileFor
      this.isUploadFileDialogOpen = true
      this.detailSelected = detailSelected
    },
    filesSelected (files) {
      this.filesToUpload = files
      this.isUploadFileDialogOpen = false
      this.isUploadFileConfirmationDialogOpen = true
    },
    confirmUploadFile (confirmation) {
      this.isUploadFileConfirmationDialogOpen = false
      if (confirmation) {
        this.uploadInvoice()
      }
    },
    uploadInvoice () {
      const formData = new FormData()
      formData.append('formFile', this.filesToUpload)
      // let query = ''
      // if (this.uploadFileFor === 'purchaseOrderLine') {
      //   query = `?detailId=${this.detailSelected.id}`
      // }
      this.loadingCard = true
      // this.$proxy.post(`api/purchaseOrders/invoices/${this.purchaseOrderId}${query}`, formData)
      this.$proxy.post(`api/purchaseOrders/invoices/${this.purchaseOrderId}/${this.detailSelected.id}`, formData)
        .then((r) => {
          this.showMessage(this.$t('message.success.invoiceUploaded'))
          this.getInvoices(this.selectedDetail)
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loadingCard = false
        })
    },
    getStatusName (statusId) {
      switch (statusId) {
        case detailState.PENDING_RECEIPT:
          return this.$t(`purchaseOrderDetailState.${detailState.PENDING_RECEIPT}`)
        case detailState.PARTIAL_RECEIPT:
          return this.$t(`purchaseOrderDetailState.${detailState.PARTIAL_RECEIPT}`)
        case detailState.COMPLETE_RECEIPT:
          return this.$t(`purchaseOrderDetailState.${detailState.COMPLETE_RECEIPT}`)
        default:
          return '-'
      }
    },
    getStatusColor (statusId) {
      switch (statusId) {
        case detailState.PENDING_RECEIPT:
          return ''
        case detailState.PARTIAL_RECEIPT:
          return 'yellow'
        case detailState.COMPLETE_RECEIPT:
          return 'green'
      }
    },
    getInvoiceStatusName (statusId) {
      switch (statusId) {
        case invoiceState.PENDING_WAREHOUSE_VALIDATION:
          return this.$t(`invoiceState.${invoiceState.PENDING_WAREHOUSE_VALIDATION}`)
        case invoiceState.PENDING_FINANCE_VALIDATION:
          return this.$t(`invoiceState.${invoiceState.PENDING_FINANCE_VALIDATION}`)
        case invoiceState.VALIDATED_INVOICE:
          return this.$t(`invoiceState.${invoiceState.VALIDATED_INVOICE}`)
        case invoiceState.REJECTED:
          return this.$t(`invoiceState.${invoiceState.REJECTED}`)
      }
    },
    getInvoiceStatusColor (statusId) {
      switch (statusId) {
        case invoiceState.PENDING_WAREHOUSE_VALIDATION:
          return ''
        case invoiceState.PENDING_FINANCE_VALIDATION:
          return 'yellow'
        case invoiceState.VALIDATED_INVOICE:
          return 'green'
        case invoiceState.REJECTED:
          return 'red'
      }
    },
    formatCurrency (q) {
      return formatCurrency(q)
    },
    downloadPdf (item) {
      this.changeLoadingStatus(true)
      this.$proxy.getDownload(`api/purchaseorders/download/pdf/${item.id}`)
        .then((r) => {
          saveAs(r.data, item.pdfFileName)
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.changeLoadingStatus(false)
        })
    },
    downloadXml (item) {
      this.changeLoadingStatus(true)
      this.$proxy.getDownload(`api/purchaseorders/download/xml/${item.id}`)
        .then((r) => {
          saveAs(r.data, item.xmlFileName)
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.changeLoadingStatus(false)
        })
    },
    onExpanded (event) {
      const { item, value } = event
      this.selectedDetail = item
      if (value && item.invoices.length === 0) {
        this.getInvoices(item)
      }
    },
    getInvoices (item) {
      this.loading.invoices = true
      this.$proxy.get(`api/invoices/order-detail/${item.id}`)
        .then(res => {
          item.invoices = res.data
        })
        .catch(e => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loading.invoices = false
        })
    }
  },
  watch: {
    purchaseOrderId: function (newVal, oldVal) {
      if (!this.isActive) {
        return
      }

      if (newVal) {
        this.purchaseOrder = {
          details: []
        }
        this.getPurchaseOrderDetail()
      }
    }
  },
  mounted () {
    this.getPurchaseOrderDetail()
  }
}
</script>

<style>
  .expanded-row th {
    background-color: #F2F2F2;
  }
</style>
